/**
 * useContentSettings Hook
 *
 * Manages local state for all 5 content settings in the publish flow.
 * Matches Flutter's ContentSettingsScreen business logic from
 * content_settings_screen.dart.
 */

import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet.index';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { ScheduleContentSheet } from '../components/steps/contentSettingsStep/ScheduleContentSheet';
import { ScheduleAction } from '../components/steps/contentSettingsStep/ScheduleContentContent';
import { LinkContentSheet } from '@/presentation/feature/content/components/sheets/linkContent/LinkContentSheet';
import { selectHasPremiumAction } from '@/presentation/store/slices/premiumPackages/premiumPackagesSlice';
import { PremiumPackageActions } from '@/domain/enums/premiumPackages/PremiumPackageActions';
import { Navigator } from '@/presentation/routing/Navigator';
import { appLogger } from '@/domain/services/analytics/appLogger';
import type { RootState } from '@/presentation/store';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { ContentSettingsResult } from '@/domain/entities/publish/contentSettingsResult';

export type { ContentSettingsResult };

interface UseContentSettingsParams {
  mediaId: string;
  screenKey?: string;
}

export const useContentSettings = ({ mediaId, screenKey }: UseContentSettingsParams) => {
  const { l10n } = useLocale();
  const hasPremiumSchedule = useSelector((state: RootState) =>
    selectHasPremiumAction(state, PremiumPackageActions.scheduleContent)
  );

  const [isDraft, setIsDraft] = useState(false);
  const [schedule, setSchedule] = useState<number | null>(null);
  const [allowEarlyAccess, setAllowEarlyAccess] = useState(false);
  const [allowComments, setAllowComments] = useState(true);
  const [showCoins, setShowCoins] = useState(true);
  const [contentLink, setContentLink] = useState<string | null>(null);
  // NOTE: linkedMedia is local-only and not emitted in ContentSettingsResult.
  // When draft-resume / edit-draft is wired up later, hydrate from `contentLink`
  // (fetch-by-path) or thread linkedMedia into ContentSettingsResult.
  const [linkedMedia, setLinkedMedia] = useState<UserMediaEntity | null>(null);

  const scheduleStatusText = isDraft
    ? l10n.publish.scheduleDraft
    : schedule != null
      ? new Date(schedule).toLocaleString()
      : l10n.publish.schedulePublishNow;

  const handleAllowEarlyAccessToggle = useCallback(async (value: boolean) => {
    if (schedule == null) {
      try {
        await InfoSheet.show({
          title: l10n.publish.earlyAccessNotAvailableTitle,
          infoText: l10n.publish.earlyAccessNotAvailableDescription,
          screenKey,
        });
      } catch (error) {
        appLogger.error('Failed to show early access info', 'useContentSettings.handleAllowEarlyAccessToggle', { error: error as Error });
      }
      return;
    }
    setAllowEarlyAccess(value);
  }, [schedule, l10n, screenKey]);

  const handleAllowCommentsToggle = useCallback((value: boolean) => {
    setAllowComments(value);
  }, []);

  const handleShowCoinsToggle = useCallback((value: boolean) => {
    setShowCoins(value);
  }, []);

  const handleSaveAsDraft = useCallback(() => {
    setIsDraft(true);
    setSchedule(null);
    setAllowEarlyAccess(false);
  }, []);

  const handlePublishNow = useCallback(() => {
    setIsDraft(false);
    setSchedule(null);
    setAllowEarlyAccess(false);
  }, []);

  const handleSchedule = useCallback((timestamp: number) => {
    setIsDraft(false);
    setSchedule(timestamp);
  }, []);

  const handleLinkContentClick = useCallback(async () => {
    try {
      const result = await LinkContentSheet.show({
        title: l10n.publish.linkContentSheetTitle,
        excludeMediaId: mediaId,
        screenKey,
      });
      if (!result) return;
      setLinkedMedia(result);
      setContentLink(result.relativePath ?? null);
    } catch (error) {
      appLogger.error('Failed to show link content sheet', 'useContentSettings.handleLinkContentClick', { error: error as Error });
    }
  }, [l10n, mediaId, screenKey]);

  const handleUnlinkContent = useCallback(() => {
    setLinkedMedia(null);
    setContentLink(null);
  }, []);

  const handleScheduleClick = useCallback(async () => {
    if (!hasPremiumSchedule) {
      try {
        const response = await ConfirmSheet.show({
          title: l10n.publish.schedulePremiumRequiredTitle,
          confirmText: l10n.publish.schedulePremiumRequiredDescription,
          okText: l10n.topBar.premiumPackages,
          cancelText: l10n.common.close,
          screenKey,
        });

        // Premium packages are currently only available on the mobile app.
        // TODO: Revisit this flow if/when premium packages are offered on web.
        // Implementation plan: add a listener for premium package updates and, if appropriate package 
        // is purchased while confirm sheet is open, automatically close the sheet and proceed to show 
        // the schedule content sheet.
        // Flutter: content_settings_screen.dart -> _onScheduleSelected()
        if (response) {
          Navigator.navigateToPremiumPackages();
        }
      } catch (error) {
        appLogger.error('Failed to show premium confirm sheet', 'useContentSettings.handleScheduleClick', { error: error as Error });
      }
      return;
    }

    try {
      const result = await ScheduleContentSheet.show({
        title: l10n.publish.scheduleContentTitle,
        initialSchedule: schedule,
        screenKey,
      });
      if (!result) return;

      switch (result.action) {
        case ScheduleAction.draft:
          handleSaveAsDraft();
          break;
        case ScheduleAction.publishNow:
          handlePublishNow();
          break;
        case ScheduleAction.schedule:
          if (result.timestamp) {
            handleSchedule(result.timestamp);
          }
          break;
      }
    } catch (error) {
      appLogger.error('Failed to show schedule sheet', 'useContentSettings.handleScheduleClick', { error: error as Error });
    }
  }, [hasPremiumSchedule, schedule, l10n, screenKey, handleSaveAsDraft, handlePublishNow, handleSchedule]);

  const buildResult = useCallback((): ContentSettingsResult => ({
    isDraft,
    schedule,
    allowEarlyAccess,
    allowComments,
    showCoins,
    contentLink,
  }), [isDraft, schedule, allowEarlyAccess, allowComments, showCoins, contentLink]);

  return {
    isDraft,
    schedule,
    allowEarlyAccess,
    allowComments,
    showCoins,
    contentLink,
    linkedMedia,
    scheduleStatusText,
    handleAllowEarlyAccessToggle,
    handleAllowCommentsToggle,
    handleShowCoinsToggle,
    handleScheduleClick,
    handleLinkContentClick,
    handleUnlinkContent,
    handleSaveAsDraft,
    handlePublishNow,
    handleSchedule,
    buildResult,
  };
};
